import { useQuery } from "@tanstack/react-query";
import { useLocation } from "wouter";
import Header from "@/components/header";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Loader2 } from "lucide-react";
import { useBitcoinBalance } from "@/hooks/use-bitcoin-balance";
import { useTranslation } from "@/i18n/LanguageContext";
import { useTitle } from "@/hooks/use-title";

interface Puzzle {
  id: number;
  title: string;
  description: string;
  bitcoinAddress: string;
  createdAt: string;
}

function PuzzleBalanceIndicator({ address }: { address: string }) {
  const { data: balance, isLoading } = useBitcoinBalance(address);
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-zinc-400">
        <Loader2 className="h-4 w-4 animate-spin" />
        {t("puzzles.checkingBalance")}
      </div>
    );
  }

  const balanceInBTC = balance ? balance / 100000000 : 0; // Convert satoshis to BTC

  if (balance === 0) {
    return <Badge variant="destructive">{t("puzzles.solved")}</Badge>;
  }

  return (
    <div className="flex flex-col items-end gap-1">
      <Badge variant="success">{t("puzzles.notSolved")}</Badge>
      <div className="text-sm text-zinc-400">
        {t("puzzles.balance")}: {balanceInBTC.toFixed(8)} BTC
      </div>
    </div>
  );
}

export default function PuzzlesPage() {
  useTitle("Bitcoin Puzzles");
  const [, setLocation] = useLocation();
  const { data: puzzles } = useQuery<Puzzle[]>({
    queryKey: ["/api/puzzles"],
  });
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-black">
      <Header />
      <main className="container mx-auto px-6 pt-24 pb-12">
        <h1 className="text-4xl font-bold text-white mb-8">{t("puzzles.title")}</h1>
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {puzzles?.map((puzzle) => (
            <Card
              key={puzzle.id}
              className="bg-zinc-900 border-zinc-800 cursor-pointer hover:bg-zinc-800 transition-colors"
              onClick={() => setLocation(`/puzzles/detail/${puzzle.id}`)}
            >
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-white">{puzzle.title}</CardTitle>
                  <PuzzleBalanceIndicator address={puzzle.bitcoinAddress} />
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-zinc-300 mb-4">{puzzle.description}</p>
                <div className="space-y-2">
                  <p className="text-sm text-zinc-400">{t("puzzles.bitcoinAddress")}:</p>
                  <code className="block bg-zinc-950 p-2 rounded text-sm font-mono break-all text-zinc-300">
                    {puzzle.bitcoinAddress}
                  </code>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </main>
    </div>
  );
}